/**
 * @file Components/Admin/QueryManager.jsx
 * @description Admin Query Manager with split-pane CRM layout.
 * Left: query list with priority tags and filters.
 * Right: conversation thread and reply interface.
 * Supports approve negotiation, status changes, and priority updates.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Filter, MessageSquare, Send, ChevronRight, Clock, CheckCircle,
  AlertCircle, Tag, Users, DollarSign, Loader2, RefreshCw, Trash2,
  ExternalLink, X, Inbox, ArrowRight, Check, Handshake
} from 'lucide-react';
import { toast } from 'react-toastify';
import queryService from '../../services/queryService';

// ─── Skeleton Loaders ──────────────────────────────────────────────────
const ListSkeleton = () => (
  <div className="animate-pulse space-y-3 p-4">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex gap-3 p-3 rounded-lg bg-gray-50">
        <div className="w-2 h-12 bg-gray-200 rounded-full" />
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

const ThreadSkeleton = () => (
  <div className="animate-pulse p-6 space-y-4">
    <div className="h-6 bg-gray-200 rounded w-1/2" />
    <div className="h-4 bg-gray-100 rounded w-1/3" />
    <div className="space-y-3 mt-6">
      {[1, 2, 3].map(i => (
        <div key={i} className={`flex ${i % 2 === 0 ? 'justify-end' : ''}`}>
          <div className={`h-16 bg-gray-100 rounded-xl w-2/3`} />
        </div>
      ))}
    </div>
  </div>
);

// ─── Priority / Status / Type helpers ──────────────────────────────────
const priorityConfig = {
  urgent: { color: 'bg-red-500', label: 'Urgent', textColor: 'text-red-700' },
  high: { color: 'bg-orange-500', label: 'High', textColor: 'text-orange-700' },
  medium: { color: 'bg-blue-500', label: 'Medium', textColor: 'text-blue-700' },
  low: { color: 'bg-gray-400', label: 'Low', textColor: 'text-gray-500' },
};

const statusConfig = {
  new: { color: 'bg-blue-100 text-blue-800', icon: <Inbox size={12} /> },
  in_progress: { color: 'bg-yellow-100 text-yellow-800', icon: <Clock size={12} /> },
  resolved: { color: 'bg-green-100 text-green-800', icon: <CheckCircle size={12} /> },
};

const typeLabels = {
  general: 'General',
  tour_specific: 'Tour Inquiry',
  negotiation: 'Negotiation',
  custom_trip: 'Custom Trip',
};

const QueryManager = () => {
  const navigate = useNavigate();
  const [queries, setQueries] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [filters, setFilters] = useState({ status: 'all', type: 'all', search: '' });
  const [approvePrice, setApprovePrice] = useState('');
  const [showApproveForm, setShowApproveForm] = useState(false);
  const threadEndRef = useRef(null);

  const fetchQueries = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.type !== 'all') params.type = filters.type;
      if (filters.search.trim()) params.search = filters.search.trim();

      const result = await queryService.getAllQueries(params);
      setQueries(result.data?.queries || []);
      if (result.stats) setStats(result.stats);
    } catch (err) {
      console.error('Error fetching queries:', err);
      toast.error('Failed to load queries');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchQueries();
  }, [fetchQueries]);

  const handleSelectQuery = async (query) => {
    setSelectedQuery(query);
    setReplyContent('');
    setShowApproveForm(false);
    // Mark as read / in_progress if new
    if (query.status === 'new') {
      try {
        await queryService.updateQuery(query._id, { status: 'in_progress' });
        setQueries(prev => prev.map(q => q._id === query._id ? { ...q, status: 'in_progress' } : q));
      } catch (err) { /* silent */ }
    }
    setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleSendReply = async () => {
    if (!replyContent.trim() || !selectedQuery) return;
    setSendingReply(true);
    try {
      const result = await queryService.replyToQuery(selectedQuery._id, replyContent.trim());
      setSelectedQuery(result.data?.query || selectedQuery);
      setReplyContent('');
      // Update in list
      setQueries(prev => prev.map(q => q._id === selectedQuery._id ? (result.data?.query || q) : q));
      setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const result = await queryService.updateQuery(id, { status });
      setQueries(prev => prev.map(q => q._id === id ? { ...q, status } : q));
      if (selectedQuery?._id === id) setSelectedQuery(prev => ({ ...prev, status }));
      toast.success(`Status updated to ${status.replace('_', ' ')}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleApproveNegotiation = async () => {
    if (!approvePrice || parseFloat(approvePrice) <= 0) {
      toast.error('Enter a valid price'); return;
    }
    try {
      const result = await queryService.approveNegotiation(selectedQuery._id, parseFloat(approvePrice));
      setSelectedQuery(result.data?.query);
      setQueries(prev => prev.map(q => q._id === selectedQuery._id ? (result.data?.query || q) : q));
      setShowApproveForm(false);
      toast.success('Negotiation approved!');
    } catch (err) {
      toast.error('Failed to approve');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this query permanently?')) return;
    try {
      await queryService.deleteQuery(id);
      setQueries(prev => prev.filter(q => q._id !== id));
      if (selectedQuery?._id === id) setSelectedQuery(null);
      toast.success('Query deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="flex h-[calc(100vh-120px)] bg-white rounded-lg shadow-md overflow-hidden">
      {/* ═══════ Left Pane: Query List ═══════ */}
      <div className="w-80 lg:w-96 border-r border-gray-200 flex flex-col flex-shrink-0">
        {/* Stats bar */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50 text-xs">
          <span className="flex items-center gap-1 text-blue-600 font-medium">
            <Inbox size={12} /> {stats.new || 0} New
          </span>
          <span className="text-gray-300">|</span>
          <span className="flex items-center gap-1 text-yellow-600">
            <Clock size={12} /> {stats.inProgress || 0}
          </span>
          <span className="text-gray-300">|</span>
          <span className="flex items-center gap-1 text-green-600">
            <CheckCircle size={12} /> {stats.resolved || 0}
          </span>
          {stats.urgent > 0 && (
            <>
              <span className="text-gray-300">|</span>
              <span className="flex items-center gap-1 text-red-600 font-bold">
                <AlertCircle size={12} /> {stats.urgent} Urgent
              </span>
            </>
          )}
        </div>

        {/* Filters */}
        <div className="p-3 border-b border-gray-100 space-y-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search queries..."
              value={filters.search}
              onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
              className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
              className="flex-1 px-2 py-1.5 border border-gray-200 rounded-md text-xs"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
            </select>
            <select
              value={filters.type}
              onChange={(e) => setFilters(f => ({ ...f, type: e.target.value }))}
              className="flex-1 px-2 py-1.5 border border-gray-200 rounded-md text-xs"
            >
              <option value="all">All Types</option>
              <option value="general">General</option>
              <option value="tour_specific">Tour Inquiry</option>
              <option value="negotiation">Negotiation</option>
              <option value="custom_trip">Custom Trip</option>
            </select>
          </div>
        </div>

        {/* Query List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? <ListSkeleton /> : queries.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400 p-6">
              <Inbox size={40} className="mb-3" />
              <p className="text-sm font-medium">No queries found</p>
              <p className="text-xs mt-1">Adjust your filters</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {queries.map(query => {
                const isActive = selectedQuery?._id === query._id;
                const pri = priorityConfig[query.priority] || priorityConfig.medium;
                const st = statusConfig[query.status] || statusConfig.new;
                const lastMsg = query.thread?.[query.thread.length - 1];

                return (
                  <button
                    key={query._id}
                    onClick={() => handleSelectQuery(query)}
                    className={`w-full text-left p-3 hover:bg-gray-50 transition-colors flex gap-3 ${
                      isActive ? 'bg-indigo-50 border-l-4 border-indigo-600' : 'border-l-4 border-transparent'
                    }`}
                  >
                    {/* Priority indicator */}
                    <div className={`w-1.5 h-full min-h-[40px] rounded-full ${pri.color} flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-semibold text-gray-900 truncate">{query.subject}</span>
                      </div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${st.color}`}>
                          {st.icon} {query.status?.replace('_', ' ')}
                        </span>
                        <span className="text-[10px] text-gray-400">{typeLabels[query.type]}</span>
                        {query.type === 'negotiation' && (
                          <Handshake size={11} className="text-amber-500" />
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500 truncate">{query.customerName}</span>
                        <span className="text-[10px] text-gray-400 flex-shrink-0">
                          {new Date(query.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ═══════ Right Pane: Conversation Thread ═══════ */}
      <div className="flex-1 flex flex-col">
        {!selectedQuery ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            <div className="text-center">
              <MessageSquare size={48} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">Select a query to view</p>
              <p className="text-sm mt-1">Choose from the list on the left</p>
            </div>
          </div>
        ) : (
          <>
            {/* Thread Header */}
            <div className="border-b border-gray-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-gray-900 text-lg truncate">{selectedQuery.subject}</h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-gray-500">
                    <span className="font-medium text-gray-700">{selectedQuery.customerName}</span>
                    <span>·</span>
                    <span>{selectedQuery.customerEmail}</span>
                    {selectedQuery.customerPhone && <><span>·</span><span>{selectedQuery.customerPhone}</span></>}
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full ${statusConfig[selectedQuery.status]?.color}`}>
                      {statusConfig[selectedQuery.status]?.icon} {selectedQuery.status?.replace('_', ' ')}
                    </span>
                    <span className={`text-xs font-medium ${priorityConfig[selectedQuery.priority]?.textColor}`}>
                      {priorityConfig[selectedQuery.priority]?.label} Priority
                    </span>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                      {typeLabels[selectedQuery.type]}
                    </span>
                    {selectedQuery.tour && (
                      <button
                        onClick={() => navigate('/admin/tours')}
                        className="text-xs text-indigo-600 hover:underline flex items-center gap-0.5"
                      >
                        {selectedQuery.tourTitle || 'View Tour'} <ExternalLink size={10} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-4 flex-shrink-0">
                  {selectedQuery.status !== 'resolved' && (
                    <button
                      onClick={() => handleStatusChange(selectedQuery._id, 'resolved')}
                      className="px-3 py-1.5 text-xs font-medium bg-green-50 text-green-700 rounded-lg hover:bg-green-100"
                    >
                      <CheckCircle size={13} className="inline mr-1" /> Resolve
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(selectedQuery._id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>

              {/* Negotiation info bar */}
              {selectedQuery.type === 'negotiation' && selectedQuery.negotiation && (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-semibold text-amber-800">Negotiation:</span>
                    <span className="text-amber-700 ml-2">
                      Original: ${selectedQuery.negotiation.originalPrice?.toLocaleString()} ·
                      Proposed: ${selectedQuery.negotiation.proposedPrice?.toLocaleString()} ·
                      {selectedQuery.negotiation.guestCount} guests
                    </span>
                    {selectedQuery.negotiation.approved && (
                      <span className="ml-2 text-green-700 font-bold">
                        ✓ Approved at ${selectedQuery.negotiation.approvedPrice?.toLocaleString()}
                      </span>
                    )}
                  </div>
                  {!selectedQuery.negotiation.approved && selectedQuery.status !== 'resolved' && (
                    <div>
                      {showApproveForm ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            placeholder="Price"
                            value={approvePrice}
                            onChange={(e) => setApprovePrice(e.target.value)}
                            className="w-24 px-2 py-1 border border-amber-300 rounded text-sm"
                          />
                          <button
                            onClick={handleApproveNegotiation}
                            className="px-3 py-1 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            Approve
                          </button>
                          <button onClick={() => setShowApproveForm(false)} className="text-gray-400 hover:text-gray-600">
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => { setApprovePrice(selectedQuery.negotiation.proposedPrice || ''); setShowApproveForm(true); }}
                          className="px-3 py-1.5 text-xs font-semibold bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                        >
                          <Check size={13} /> Approve & Send Payment Link
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Custom trip info */}
              {selectedQuery.type === 'custom_trip' && selectedQuery.customTrip && (
                <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm text-purple-700">
                  <span className="font-semibold">Custom Trip:</span>
                  {selectedQuery.customTrip.budget > 0 && <span className="ml-2">Budget: ${selectedQuery.customTrip.budget?.toLocaleString()}</span>}
                  {selectedQuery.customTrip.groupSize > 0 && <span className="ml-2">· {selectedQuery.customTrip.groupSize} travelers</span>}
                  {selectedQuery.customTrip.interests?.length > 0 && (
                    <span className="ml-2">· Interests: {selectedQuery.customTrip.interests.join(', ')}</span>
                  )}
                </div>
              )}
            </div>

            {/* Thread Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {selectedQuery.thread?.map((msg, i) => (
                <div key={i} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${
                    msg.sender === 'admin'
                      ? 'bg-indigo-600 text-white rounded-br-md'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}>
                    <div className={`text-xs mb-1 ${msg.sender === 'admin' ? 'text-indigo-200' : 'text-gray-400'}`}>
                      {msg.senderName || msg.sender} · {new Date(msg.createdAt).toLocaleString('en-US', {
                        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                      })}
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                </div>
              ))}
              <div ref={threadEndRef} />
            </div>

            {/* Reply Input */}
            {selectedQuery.status !== 'resolved' && (
              <div className="border-t border-gray-200 p-4 bg-white">
                <div className="flex gap-3">
                  <textarea
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Type your reply..."
                    rows={2}
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none"
                    onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSendReply(); }}
                  />
                  <button
                    onClick={handleSendReply}
                    disabled={!replyContent.trim() || sendingReply}
                    className="self-end px-5 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-all flex items-center gap-2 font-medium text-sm"
                  >
                    {sendingReply ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    Send
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Ctrl+Enter to send</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QueryManager;
